import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AppUserProfileResponse } from '../models/auth.model';

interface PersistedAuthSession {
   accessToken: string;
   profile: AppUserProfileResponse;
}

@Injectable({ providedIn: 'root' })
export class AuthStore {
   private static readonly SESSION_KEY = 'kcms-loyalty-auth-session';

   private accessToken: string | null = null;
   private profileSubject = new BehaviorSubject<AppUserProfileResponse | null>(null);
   private initializedSubject = new BehaviorSubject<boolean>(false);

   readonly profileChanged$ = this.profileSubject.asObservable();
   readonly initialized$ = this.initializedSubject.asObservable();

   constructor() {
      this.restoreFromSessionStorage();
   }

   get token(): string | null {
      return this.accessToken;
   }

   get profile(): AppUserProfileResponse | null {
      return this.profileSubject.value;
   }

   get isAuthenticated(): boolean {
      return !!this.accessToken && !!this.profileSubject.value;
   }

   get hasValidAccessToken(): boolean {
      return this.isTokenValid(this.accessToken);
   }

   get isInitialized(): boolean {
      return this.initializedSubject.value;
   }

   setSession(accessToken: string, profile: AppUserProfileResponse): void {
      this.accessToken = accessToken;
      this.profileSubject.next(profile);
      this.initializedSubject.next(true);
      this.persistSession(accessToken, profile);
   }

   setProfile(profile: AppUserProfileResponse): void {
      this.profileSubject.next(profile);
      if (this.accessToken) {
         this.persistSession(this.accessToken, profile);
      }
   }

   clear(): void {
      this.accessToken = null;
      this.profileSubject.next(null);
      this.initializedSubject.next(true);
      this.clearPersistedSession();
   }

   private restoreFromSessionStorage(): void {
      try {
         const raw = sessionStorage.getItem(AuthStore.SESSION_KEY);
         if (!raw) {
            return;
         }

         const session = JSON.parse(raw) as PersistedAuthSession;
         if (!session?.accessToken || !session?.profile || !this.isTokenValid(session.accessToken)) {
            this.clearPersistedSession();
            return;
         }

         this.accessToken = session.accessToken;
         this.profileSubject.next(session.profile);
         this.initializedSubject.next(true);
      } catch {
         this.clearPersistedSession();
      }
   }

   private persistSession(accessToken: string, profile: AppUserProfileResponse): void {
      try {
         const session: PersistedAuthSession = { accessToken, profile };
         sessionStorage.setItem(AuthStore.SESSION_KEY, JSON.stringify(session));
      } catch {
         // Ignore quota / private mode failures; in-memory session still works.
      }
   }

   private clearPersistedSession(): void {
      try {
         sessionStorage.removeItem(AuthStore.SESSION_KEY);
      } catch {
         // Ignore storage access failures.
      }
   }

   private isTokenValid(accessToken: string | null): boolean {
      if (!accessToken) {
         return false;
      }

      try {
         const encodedPayload = accessToken.split('.')[1];
         if (!encodedPayload) {
            return false;
         }

         const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
         const payload = JSON.parse(atob(base64));
         return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now() + 30000;
      } catch {
         return false;
      }
   }
}
